import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Theme, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { sharedTableStyles } from '../Spectable.styles'
import DraggableRow from './DraggableRow'
import {
    IdTitleProps,
    PinningProps,
    tableSettingsStyles,
} from './TableSettings'
import TableSettingsButton from './TableSettingsButton'

export const droppableContainerStyles = makeStyles((theme: Theme) => ({
    columnHeader: {
        backgroundColor: '#0069AA',
        color: '#fff',
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(0.5),
    },
    contentTitleBorder: {
        borderBottom: '1px solid #c4c4c4',
    },
    droppableContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: '1px solid #c4c4c4',
        marginBottom: 10,
        minHeight: pxToRem(40),
    },
    gridItemMargin: {
        marginRight: theme.spacing(1),
    },
    centerText: {
        textAlign: 'center',
    },
}))

export interface DroppableContainerProps {
    droppableId: string
    items: IdTitleProps[]
    orderPrefix?: string
    pinningProps: PinningProps
    pinOnFirstItem: boolean
    pinOnLastItem: boolean
    showContentHeader: boolean
    showPinning: boolean
    title: string
    showCollapseToggle?: boolean
    collapseColumnCheckedState?: Record<string, boolean> | undefined
    handlePinClick: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    handleRemoveAllClick: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    handleRemoveClick: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    handleCollapseCheck?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => void
}

export const DroppableContainer = ({
    droppableId,
    items,
    orderPrefix = '',
    pinningProps,
    pinOnFirstItem,
    pinOnLastItem,
    showContentHeader,
    showPinning,
    title,
    showCollapseToggle,
    collapseColumnCheckedState,
    handlePinClick,
    handleRemoveAllClick,
    handleRemoveClick,
    handleCollapseCheck,
}: DroppableContainerProps) => {
    const droppableContainerClasses = droppableContainerStyles()
    const tableSettingsClasses = tableSettingsStyles()
    const sharedTableClasses = sharedTableStyles()
    const lastItemIdx = items.length - 1

    const { setNodeRef } = useDroppable({
        id: droppableId,
    })

    return (
        <SortableContext
            id={droppableId}
            items={items}
            strategy={verticalListSortingStrategy}
        >
            <div
                className={clsx(
                    sharedTableClasses.fullWidth,
                    droppableContainerClasses.droppableContainer,
                    'column-container'
                )}
            >
                <div
                    className={clsx(
                        sharedTableClasses.fullWidth,
                        droppableContainerClasses.columnHeader,
                        'column-header'
                    )}
                >
                    <GridContainer
                        alignItems="center"
                        className={tableSettingsClasses.rowHeight}
                    >
                        <GridItem
                            xs={8}
                            className={droppableContainerClasses.gridItemMargin}
                        >
                            {title}
                        </GridItem>
                        <GridItem>
                            <TableSettingsButton
                                data-droppable-id={droppableId}
                                onClick={handleRemoveAllClick}
                            >
                                Remove All
                            </TableSettingsButton>
                        </GridItem>
                    </GridContainer>
                </div>
                {showContentHeader && (
                    <div
                        className={clsx(
                            sharedTableClasses.fullWidth,
                            tableSettingsClasses.rowTitle,
                            'column-content-title'
                        )}
                    >
                        <GridContainer
                            alignItems="center"
                            wrap={'nowrap'}
                            className={clsx(
                                tableSettingsClasses.rowHeight,
                                tableSettingsClasses.rowPadding,
                                droppableContainerClasses.contentTitleBorder
                            )}
                        >
                            <GridItem xs={showCollapseToggle ? 5 : 7}>
                                Name
                            </GridItem>
                            <GridItem
                                xs={2}
                                className={droppableContainerClasses.centerText}
                            >
                                {showPinning && 'Pin'}
                            </GridItem>
                            <GridItem
                                xs={2}
                                className={droppableContainerClasses.centerText}
                            >
                                Order
                            </GridItem>
                            <GridItem
                                xs={2}
                                className={droppableContainerClasses.centerText}
                            >
                                Remove
                            </GridItem>
                            {showCollapseToggle && (
                                <GridItem
                                    className={`${droppableContainerClasses.centerText}`}
                                >
                                    Collapse
                                </GridItem>
                            )}
                        </GridContainer>
                    </div>
                )}
                <div
                    className={clsx(
                        sharedTableClasses.fullWidth,
                        'column-content-list'
                    )}
                    ref={setNodeRef}
                    data-testid={`column-${droppableId}`}
                >
                    {items.map((item, idx) => {
                        const collapseChecked =
                            showCollapseToggle && !!collapseColumnCheckedState
                                ? collapseColumnCheckedState![item.id]
                                : false
                        const pinActiveFirstColumn =
                            pinningProps.firstColumnPinned &&
                            ((items.length === 1 && pinOnFirstItem) ||
                                (items.length > 1 && idx === 0))
                        const pinActiveLastColumn =
                            pinningProps.lastColumnPinned &&
                            ((items.length === 1 && !pinOnFirstItem) ||
                                (items.length > 1 && idx === lastItemIdx))
                        const pinActive =
                            pinActiveFirstColumn || pinActiveLastColumn
                        const showPinComponent =
                            showPinning &&
                            ((pinOnFirstItem && idx === 0) ||
                                (pinOnLastItem && idx === lastItemIdx))

                        return (
                            <DraggableRow
                                droppableId={droppableId}
                                key={item.id}
                                displayName={item.title}
                                index={idx}
                                id={item.id}
                                orderPrefix={orderPrefix}
                                pinActive={pinActive}
                                showPinComponent={showPinComponent}
                                showCollapseToggle={showCollapseToggle}
                                collapseColumnChecked={collapseChecked}
                                handlePinClick={handlePinClick}
                                handleRemoveClick={handleRemoveClick}
                                handleCollapseCheck={handleCollapseCheck}
                            />
                        )
                    })}
                    {items?.length === 0 && (
                        <div style={{ minHeight: pxToRem(40) }} />
                    )}
                </div>
            </div>
        </SortableContext>
    )
}
